// src/pages/TutorPage.jsx
import React, { useState } from 'react';
import Header from '../components/Header';
// Asegúrate de que la ruta sea correcta hacia tu componente SpeakingExerciseFree
import SpeakingExerciseFree from '../exercises/SpeakingExerciseFree';
import './TutorPage.css';
import usePageTracker from "../hooks/usePageTracker.js"; // 🔌 INYECCIÓN DEL RADAR

// Importamos los iconos necesarios de Lucide
import {
    MessageCircle, Coffee, MapPin, ShoppingBag, Plane,
    Siren, HeartCrack, Gavel, Skull, Rocket,
    Briefcase, Zap, TrendingUp, Mic, UserX,
    Flame, AlertTriangle, Ghost, Search, Key,
    Smile, Gamepad2, Headphones, Globe, Sparkles, Tv
} from 'lucide-react';

// ==========================================
// 📚 DATA: EL MENÚ DE EXPERIENCIAS WOW
// ==========================================
const topicsData = {
    // 1. BÁSICOS Y ÚTILES
    essentials: [
        { id: 'free', icon: <MessageCircle size={20} />, name: 'Conversación Libre', description: 'Habla de lo que quieras, sin guion.' },
        { id: 'brilliant_minds', icon: <Sparkles size={20} />, name: 'Mentes Brillantes', description: 'Habla con famosos, genios o personajes históricos.' },
        { id: 'greetings', icon: <Smile size={20} />, name: 'Romper el Hielo', description: 'Aprende a presentarte con carisma.' },
        { id: 'restaurant', icon: <Coffee size={20} />, name: 'Restaurante VIP', description: 'Ordena como un experto culinario.' },
        { id: 'travel', icon: <Plane size={20} />, name: 'Aeropuerto Global', description: 'Check-in, aduanas y embarque.' },
    ],

    // 2. 🔥 SURVIVAL MODE (ROJO INTENSO)
    survival: [
        { id: 'police', icon: <Siren size={20} />, name: 'El Interrogatorio', description: 'La policía cree que eres un espía. ¡Explícate!' },
        { id: 'hospital', icon: <AlertTriangle size={20} />, name: 'Urgencias Médicas', description: 'Describe tus síntomas extraños al doctor.' },
        { id: 'lost_passport', icon: <UserX size={20} />, name: 'Pasaporte Perdido', description: 'Convence a la embajada de tu identidad.' },
        { id: 'missed_flight', icon: <ClockIcon size={20} />, name: 'Vuelo Perdido', description: 'Negocia un nuevo vuelo sin pagar extra.' },
    ],

    // 3. 💼 CORPORATE SHARK (AZUL OSCURO)
    business: [
        { id: 'raise', icon: <TrendingUp size={20} />, name: 'El Aumento', description: 'Convence a tu jefe tacaño de subirte el sueldo.' },
        { id: 'shark_tank', icon: <Briefcase size={20} />, name: 'Shark Tank Pitch', description: 'Vende tu idea millonaria en 1 minuto.' },
        { id: 'firing', icon: <UserX size={20} />, name: 'El Despido', description: 'Despide a alguien amablemente (o no).' },
        { id: 'pr_crisis', icon: <Mic size={20} />, name: 'Crisis de Prensa', description: 'Tu empresa cometió un error. Calma a los medios.' },
    ],

    // 4. 🤬 GESTIÓN DE IRA (NARANJA QUEMADO)
    anger: [
        { id: 'bad_food', icon: <UtensilsCrossed size={20} />, name: 'La Sopa Fría', description: 'Reclama al Chef arrogante en un restaurante de lujo.' },
        { id: 'noisy_neighbor', icon: <VolumeX size={20} />, name: 'Vecino Ruidoso', description: 'Son las 3 AM. Haz que apaguen la música.' },
        { id: 'refund', icon: <Laptop size={20} />, name: 'Reembolso Tech', description: 'Pelea por tu dinero con soporte técnico.' },
        { id: 'taxi_scam', icon: <MapPin size={20} />, name: 'Estafa en el Taxi', description: 'El conductor quiere cobrarte el triple. ¡No!' },
    ],

    // 5. ❤️ AMOR Y DRAMA (ROSA/MAGENTA)
    drama: [
        { id: 'breakup', icon: <HeartCrack size={20} />, name: 'La Ruptura', description: 'Termina la relación sin romperle el corazón.' },
        { id: 'blind_date', icon: <GlassWater size={20} />, name: 'Cita a Ciegas', description: 'Sobrevive a una cita llena de silencios incómodos.' },
        { id: 'cheating', icon: <Search size={20} />, name: 'Detectando Mentiras', description: 'Confronta a tu amigo/a por esos mensajes sospechosos.' },
    ],

    // 6. 🕵️‍♂️ MISTERIO Y CRIMEN (VIOLETA)
    mystery: [
        { id: 'witness', icon: <Eye size={20} />, name: 'Testigo Ocular', description: 'Describe al sospechoso al dibujante de la policía.' },
        { id: 'ghost', icon: <Ghost size={20} />, name: 'Hotel Embrujado', description: 'Eres recepcionista. Un huésped vio un fantasma.' },
        { id: 'sherlock', icon: <Search size={20} />, name: 'Modo Sherlock', description: 'Interroga a 3 sospechosos. ¿Quién se comió el pastel?' },
        { id: 'escape_room', icon: <Key size={20} />, name: 'Escape Room', description: 'Resuelve acertijos lógicos para abrir la puerta.' },
    ],

    // 7. 👽 SCI-FI & FUTURO (CYAN/NEÓN)
    scifi: [
        { id: 'alien', icon: <Rocket size={20} />, name: 'Primer Contacto', description: 'Explícale a un alien qué es el "amor" o la "guerra".' },
        { id: 'robot_rebel', icon: <BotIcon size={20} />, name: 'Robot Rebelde', description: 'Tu IA doméstica se niega a abrir la puerta.' },
        { id: 'mars', icon: <Globe size={20} />, name: 'Colonia Marte', description: 'Eres el líder. Se acabó el agua. Da un discurso.' },
    ],

    // 8. 🕹️ CULTURA POP (AMARILLO)
    pop: [
        { id: 'influencer', icon: <Smartphone size={20} />, name: 'Disculpa Influencer', description: 'Hiciste algo mal. Graba tu video de disculpa.' },
        { id: 'gaming', icon: <Gamepad2 size={20} />, name: 'Gamer Rage', description: 'Trash talk respetuoso contra tu oponente online.' },
        // Sustituye a «Análisis Trap». Rob, 29-09-2026: «en realidad son muchísimas
        // canciones de trap y son groseras». Tenía razón y no era sólo cuestión
        // de gusto: el escenario le pedía al alumno traer una letra, y NSL no
        // controla cuál — con la declaración de edad recién puesta, apuntar a un
        // género entero y esperar que el alumno elija la letra limpia no es una
        // decisión que se pueda sostener.
        //
        // «Sin Spoilers» ocupa su sitio porque la restricción es la clase: para
        // convencer sin destripar hay que resumir, elegir qué callar y vender
        // una idea, que es hablar de verdad. Y el alumno trae lo que quiera sin
        // que nadie tenga que revisarlo.
        { id: 'spoilers', icon: <Tv size={20} />, name: 'Sin Spoilers', description: 'Convence a alguien de ver tu serie favorita sin contarle el final.' },
    ]
};

// Iconos auxiliares para uso interno si no están importados arriba
function ClockIcon({ size }) { return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>; }
function UtensilsCrossed({ size }) { return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8" /><path d="M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7" /><path d="m2.1 21.8 6.4-6.3" /><path d="m19 5-7 7" /></svg>; }
function VolumeX({ size }) { return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>; }
function Laptop({ size }) { return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9m16 0H4m16 0 1.28 2.55a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45L4 16" /></svg>; }
function GlassWater({ size }) { return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15.2 22H8.8a2 2 0 0 1-2-1.79L5 3h14l-1.81 17.21A2 2 0 0 1 15.2 22Z" /><path d="M6 12h12" /><path d="M5.6 17h12.8" /></svg>; }
function Eye({ size }) { return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>; }
function BotIcon({ size }) { return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="10" x="3" y="11" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /><line x1="8" x2="8" y1="16" y2="16" /><line x1="16" x2="16" y1="16" y2="16" /></svg>; }
function Smartphone({ size }) { return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2" /><path d="M12 18h.01" /></svg>; }


// ==========================================
// 🎨 COMPONENTES VISUALES
// ==========================================

function AnimatedBackground() {
    return (
        <div className="tp-background">
            <div className="tp-stars"></div>
        </div>
    );
}

function TopicCard({ topic, isActive, onClick, colorTheme }) {
    // Aplicamos el color de tema al borde activo o al icono
    const style = isActive ? { borderColor: colorTheme, boxShadow: `0 0 15px ${colorTheme}40` } : {};
    const iconStyle = { color: isActive ? colorTheme : 'var(--text-secondary)' };

    return (
        <div className={`tp-topic-card ${isActive ? 'active' : ''}`} onClick={onClick} style={style}>
            <div className="tp-topic-header">
                <span className="tp-topic-icon" style={iconStyle}>{topic.icon}</span>
                <span className="tp-topic-name">{topic.name}</span>
            </div>
            <p className="tp-topic-description">{topic.description}</p>
        </div>
    );
}

function TopicSection({ title, topics, activeTopic, onSelect, color }) {
    return (
        <div className="tp-topic-section">
            <h3 className="tp-section-title" style={{ color: color, borderColor: color }}>{title}</h3>
            <div className="tp-topic-grid">
                {topics.map(topic => (
                    <TopicCard
                        key={topic.id}
                        topic={topic}
                        isActive={activeTopic.id === topic.id}
                        onClick={() => onSelect(topic)}
                        colorTheme={color}
                    />
                ))}
            </div>
        </div>
    );
}

function TopicSelectionPanel({ activeTopic, onTopicSelect }) {
    return (
        <aside className="tp-left-panel">
            <div className="tp-panel-header">
                <div className="tp-spacy-avatar-wrapper">
                    <div className="tp-spacy-avatar">🤖</div>
                    <div className="tp-status-dot"></div>
                </div>
                <div className="tp-panel-title">
                    <h2>Spacy Lab</h2>
                    <p>Elige tu aventura</p>
                </div>
            </div>

            <div className="tp-scroll-container">
                <TopicSection title="🌟 Esenciales" topics={topicsData.essentials} activeTopic={activeTopic} onSelect={onTopicSelect} color="#fbbf24" />
                <TopicSection title="🔥 Supervivencia" topics={topicsData.survival} activeTopic={activeTopic} onSelect={onTopicSelect} color="#ef4444" />
                <TopicSection title="💼 Corporate Shark" topics={topicsData.business} activeTopic={activeTopic} onSelect={onTopicSelect} color="#3b82f6" />
                <TopicSection title="🤬 Gestión de Ira" topics={topicsData.anger} activeTopic={activeTopic} onSelect={onTopicSelect} color="#f97316" />
                <TopicSection title="❤️ Amor & Drama" topics={topicsData.drama} activeTopic={activeTopic} onSelect={onTopicSelect} color="#ec4899" />
                <TopicSection title="🕵️‍♂️ Misterio & Crimen" topics={topicsData.mystery} activeTopic={activeTopic} onSelect={onTopicSelect} color="#8b5cf6" />
                <TopicSection title="👽 Sci-Fi & Futuro" topics={topicsData.scifi} activeTopic={activeTopic} onSelect={onTopicSelect} color="#06b6d4" />
                <TopicSection title="🕹️ Gen Z & Pop" topics={topicsData.pop} activeTopic={activeTopic} onSelect={onTopicSelect} color="#d946ef" />
            </div>
        </aside>
    );
}

// ==========================================
// 🚀 PÁGINA PRINCIPAL
// ==========================================

function TutorPage() {
    // 🔌 ACTIVACIÓN DEL RADAR
    usePageTracker("tutor", "Tutor IA (Público)", null, "in_spacy");

    // Estado inicial: Conversación Libre
    const [activeTopic, setActiveTopic] = useState(topicsData.essentials[0]);

    const handleTopicChange = (topic) => {
        // Pequeña vibración si es móvil (haptic feedback)
        if (navigator.vibrate) navigator.vibrate(10);
        setActiveTopic(topic);
    };

    return (
        <div className="tp-tutor-page-container">
            <AnimatedBackground />
            <Header />

            <div className="tp-tutor-layout">
                {/* Panel Izquierdo: Menú Épico */}
                <TopicSelectionPanel
                    activeTopic={activeTopic}
                    onTopicSelect={handleTopicChange}
                />

                {/* Panel Derecho: El Chat */}
                <main className="tp-right-panel">
                    <div className="tp-chat-container">
                        {/* 🔥 MAGIC KEY: Al cambiar el ID, React destruye el componente viejo
                           y monta uno nuevo. Esto reinicia el chat, el launchpad y todo el estado.
                        */}
                        <SpeakingExerciseFree
                            key={activeTopic.id}
                            exerciseId={`free-mode-${activeTopic.id}`}
                            exerciseData={{
                                data: {
                                    title: activeTopic.name,
                                    user_level: "B1", // Esto luego puede venir de la DB
                                    learning_objectives: [activeTopic.description]
                                }
                            }}
                            onCorrectAnswer={() => console.log("Sesión completada")}
                        />
                    </div>
                </main>
            </div>
        </div>
    );
}

export default TutorPage;